import FooterLinkedInLink from "../components/FooterLinkedInLink";
import "../ScrollAnimations.css";
import ImageContainer from "../components/ImageContainer";
import StructuredData from "../components/StructuredData";
import CompareWebsiteClosing from "../components/CompareWebsiteClosing";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { isMacOS, isSafari } from "../player/Utils";

// Fonction pour détecter la langue du navigateur
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0].toLowerCase();
  return langCode === 'fr' ? 'fr' : 'en';
};

const CALENDLY_DEMO_URL = "https://calendly.com/jacques-upreality/30min";

function openCalendlyDemo(analyticsManager, origin) {
  analyticsManager.logHPRequestDemoTag(origin);
  window.open(CALENDLY_DEMO_URL, "_blank", "noopener,noreferrer");
}

const INSTANT_AD_BG_GREY = "#f7f9fc";
const INSTANT_AD_BG_WHITE = "#ffffff";
const INSTANT_AD_BRAND_GREEN = "#2fc4b5";
const INSTANT_AD_BRAND_GREEN_SOFT = "rgba(4, 211, 188, 0.12)";

// Fond clair inspiré du dégradé logo (mint → teal → bleu) — hero + slide « Pour qui ? »
const HP_SLIDE_BG_HERO = "linear-gradient(165deg, #e8faf4 0%, #eaf7fb 52%, #eef3fc 100%)";

const instantAdSlideTitleStyle = {
  marginTop: 0,
  marginBottom: 48,
  fontSize: 30,
  fontWeight: 700,
  color: "rgb(45, 55, 72)",
  textAlign: "center",
  lineHeight: 1.2,
};

const instantAdSlideTitleStyleCompact = {
  ...instantAdSlideTitleStyle,
  marginBottom: 32,
  fontSize: 24,
};

function instantAdSlideShellStyle(background, { compact = false, fullViewport = false } = {}) {
  const isGradient = typeof background === "string" && background.includes("gradient");
  return {
    marginTop: 0,
    width: "100%",
    ...(isGradient ? { background } : { backgroundColor: background }),
    padding: compact ? "28px 16px 40px" : "80px 24px 80px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    ...(fullViewport && !compact ? { minHeight: window.innerHeight, justifyContent: "center" } : {}),
  };
}

const HP_DESKTOP_VISUAL_COMPACT_SCALE = 0.9;
const HP_DESKTOP_VISUAL_COMPACT_MAX_HEIGHT = 880;

function scaleHpDesktopVisual(value, scale = 1) {
  return Math.round(value * scale);
}

function getHpDesktopVisualMetrics(deskTop, { compactLaptop = false } = {}) {
  const scale = compactLaptop ? HP_DESKTOP_VISUAL_COMPACT_SCALE : 1;
  const s = (value) => scaleHpDesktopVisual(value, scale);
  // compactLaptop : toujours partir des métriques « grand téléphone » avant le scale (évite un 2e palier < 688 px)
  const useLargePhoneMetrics = deskTop || compactLaptop;
  const deviceBgHeight = useLargePhoneMetrics ? s(576) : s(516);
  const storyHeight = useLargePhoneMetrics ? s(540) : s(486);
  const storyTopMargin = useLargePhoneMetrics ? s(16) : s(14);
  const frameWidth = s(274);
  const playerWidth = s(244);
  const frameMarginLeft = s(14);
  const plancheWidth = s(160);
  return {
    scale,
    deviceBgHeight,
    storyHeight,
    storyTopMargin,
    frameWidth,
    playerWidth,
    frameMarginLeft,
    plancheWidth,
    heroArrowWidth: s(72),
    heroCalloutMarginTop: storyTopMargin + s(250),
    heroCalloutMaxWidth: s(160),
    createSlideImageHeight: s(450),
    createSlideVideoWidth: s(455),
  };
}

function renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight, frameAlt = "", id }) {
  return (
    <img
      id={id}
      src="/images/transparent_framed.png"
      alt={frameAlt}
      draggable={false}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: frameWidth,
        height: deviceBgHeight,
        pointerEvents: "none",
        zIndex: 1,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
    />
  );
}

const HP_PHONE_PLAYER_BLEED_PX = 1;

function getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight) {
  return {
    width: frameWidth,
    height: deviceBgHeight,
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
    userSelect: "none",
    WebkitUserSelect: "none",
  };
}

function getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin) {
  return {
    marginLeft: Math.max(0, frameMarginLeft - HP_PHONE_PLAYER_BLEED_PX),
    position: "relative",
    top: Math.max(0, storyTopMargin - HP_PHONE_PLAYER_BLEED_PX),
  };
}

function getHpPhonePlayerBleedSize(playerWidth, storyHeight) {
  return {
    width: playerWidth + HP_PHONE_PLAYER_BLEED_PX * 2,
    height: storyHeight + HP_PHONE_PLAYER_BLEED_PX * 2,
  };
}

const INSTANT_AD_WHO_FOR_MOCKUP_CARDS = [
  { titleKey: "InstantAdWhoForRestaurantsTitle", detKey: "InstantAdWhoForRestaurantsDet", delay: "100", variant: "featured", decor: "bottomLeftCircle" },
  { titleKey: "InstantAdWhoForAgenciesTitle", detKey: "InstantAdWhoForAgenciesDet", delay: "200", variant: "default", decor: "rightDiamond" },
  { titleKey: "InstantAdWhoForChainTitle", detKey: "InstantAdWhoForChainDet", delay: "300", variant: "default", decor: "topRightCircle" },
];

function renderWhoForMockupDecor(decor, isFeatured, compact = false) {
  if (compact) {
    if (decor === "topRightCircle") {
      return (
        <div style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 72,
          height: 72,
          borderRadius: "50%",
          backgroundColor: "rgba(4, 211, 188, 0.08)",
          pointerEvents: "none",
        }} />
      );
    }
    if (decor === "bottomLeftCircle") {
      return (
        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: 80,
          height: 80,
          borderRadius: "50%",
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          pointerEvents: "none",
        }} />
      );
    }
    return (
      <div style={{
        position: "absolute",
        top: "50%",
        right: 0,
        width: 56,
        height: 56,
        borderRadius: 8,
        transform: "translateY(-50%) rotate(45deg)",
        backgroundColor: isFeatured ? "rgba(255, 255, 255, 0.14)" : INSTANT_AD_BRAND_GREEN_SOFT,
        pointerEvents: "none",
      }} />
    );
  }

  if (decor === "topRightCircle") {
    return (
      <div style={{
        position: "absolute",
        top: -40,
        right: -40,
        width: 160,
        height: 160,
        borderRadius: "50%",
        backgroundColor: "rgba(4, 211, 188, 0.08)",
        pointerEvents: "none",
      }} />
    );
  }
  if (decor === "bottomLeftCircle") {
    return (
      <div style={{
        position: "absolute",
        bottom: -40,
        left: -40,
        width: 192,
        height: 192,
        borderRadius: "50%",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        pointerEvents: "none",
      }} />
    );
  }
  return (
    <div style={{
      position: "absolute",
      top: "50%",
      right: -48,
      width: 128,
      height: 128,
      borderRadius: 8,
      transform: "translateY(-50%) rotate(45deg)",
      backgroundColor: isFeatured ? "rgba(255, 255, 255, 0.14)" : INSTANT_AD_BRAND_GREEN_SOFT,
      pointerEvents: "none",
    }} />
  );
}

function renderWhoForMockupCard(t, { titleKey, detKey, delay, variant, decor }, compact = false) {
  const isFeatured = variant === "featured";
  return (
    <div
      key={titleKey}
      className={`hp-who-for-card scroll-benefit-item scroll-delay-${delay}`}
      style={{
        position: "relative",
        overflow: compact ? "visible" : "hidden",
        padding: compact ? "28px 24px" : "40px",
        borderRadius: 24,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        flex: compact ? "1 1 auto" : "1 1 300px",
        width: compact ? "100%" : undefined,
        maxWidth: compact ? "100%" : 380,
        minWidth: 0,
        boxSizing: "border-box",
        background: isFeatured ? INSTANT_AD_BRAND_GREEN : "#ffffff",
        color: isFeatured ? "#ffffff" : "rgb(45, 55, 72)",
        border: isFeatured ? "none" : "1px solid rgba(187, 202, 198, 0.5)",
        boxShadow: isFeatured ? "0 25px 50px -12px rgba(4, 211, 188, 0.28)" : "none",
        // Pas de transform ici : ça crée un scrollport qui bloque la molette / le touch sur la page
        zIndex: compact ? "auto" : (isFeatured ? 10 : 1),
      }}
    >
      {renderWhoForMockupDecor(decor, isFeatured, compact)}
      <h3 style={{
        margin: 0,
        marginBottom: compact ? 12 : 16,
        fontSize: compact ? 18 : 22,
        fontWeight: 700,
        lineHeight: 1.25,
        position: "relative",
        zIndex: 10,
      }}>
        {t(titleKey)}
      </h3>
      <p style={{
        margin: 0,
        fontSize: compact ? 14 : 15,
        lineHeight: 1.5,
        fontWeight: 400,
        color: isFeatured ? "rgba(255, 255, 255, 0.9)" : "#3c4947",
        position: "relative",
        zIndex: 10,
        whiteSpace: "pre-line",
      }}>
        {t(detKey)}
      </p>
    </div>
  );
}

function renderWhoForMockupSlide(t, { MaterialButton, analyticsManager, navigate, compact = false, denseTop = false, backgroundColor = INSTANT_AD_BG_WHITE }) {
  const shellCompact = compact || denseTop;
  const titleStyle = compact
    ? instantAdSlideTitleStyleCompact
    : denseTop
      ? { ...instantAdSlideTitleStyle, marginTop: 18, marginBottom: 46 }
      // desktop non-compact : même écart titre→contenu que début de slide→titre (shell 80)
      : { ...instantAdSlideTitleStyle, marginBottom: 80 };
  return (
    <div
      className="scroll-slide-up hp-who-for-slide"
      style={instantAdSlideShellStyle(backgroundColor, { compact: shellCompact })}
    >
      <h2 style={titleStyle}>
        {t("InstantAdWhoForTitle")}
      </h2>
      <div style={{
        display: "flex",
        flexDirection: compact ? "column" : "row",
        justifyContent: "center",
        alignItems: compact ? "stretch" : "stretch",
        gap: compact ? 16 : 32,
        maxWidth: 1200,
        width: "100%",
        margin: "0 auto",
        flexWrap: "wrap",
      }}>
        {INSTANT_AD_WHO_FOR_MOCKUP_CARDS.map((card) => renderWhoForMockupCard(t, card, compact))}
      </div>
      <div
        className="scroll-fade-up hp-who-for-cta"
        style={{
          marginTop: 48,
          display: "flex",
          flexDirection: compact ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          gap: compact ? 20 : 16,
          flexWrap: "wrap",
        }}
      >
        <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemoBtm")} value={t("CTA")} />
        <MaterialButton style="outlined-primary" onClick={() => { analyticsManager.logHPTryMakerTag("bottom_ad"); navigate("/maker"); }} value={t("InstantAdCTA")} />
      </div>
    </div>
  );
}

const INSTANT_AD_CREATE_STEPS = [
  { step: 1, titleKey: "InstantAdSubBenefit21", detVariant: "create1ClicLink", delay: "200" },
  { step: 2, titleKey: "InstantAdSubBenefit22", detKey: "InstantAdSubBenefit22det", delay: "250" },
  { step: 3, titleKey: "InstantAdSubBenefit23", detKey: "InstantAdSubBenefit23det", delay: "300" },
  { step: 4, titleKey: "InstantAdSubBenefit24", detKey: "InstantAdSubBenefit24det", delay: "350" },
  { step: 5, titleKey: "InstantAdSubBenefit25", detKey: "InstantAdSubBenefit25det", delay: "400" },
];

const INSTANT_AD_PLANCHE_ITEMS = [
  { emoji: "🎬", titleKey: "RestaurantsSubBenefit11", delay: "100", side: "left", topPct: 20 },
  { emoji: "📋", titleKey: "RestaurantsSubBenefit12", delay: "200", side: "left", topPct: 50, topPct2: 94, multiline: true },
  { emoji: "⭐", titleKey: "RestaurantsSubBenefit13", delay: "300", side: "right", topPct: 74, topPctCompactLaptop: 74 },
  { emoji: "💬", titleKey: "RestaurantsSubBenefit14", delay: "400", side: "right", topPct: 80, topPctCompactLaptop: 80, nowrap: true },
];

function plancheScrollItemClass(delay, animated = true) {
  return animated ? `scroll-benefit-item scroll-delay-${delay}` : undefined;
}

function renderPlancheMultilineLeftAnnotation(t, { titleKey, delay, topPct, topPct2 }, { storyTopMargin, storyHeight, compact = false, animated = true }) {
  const fullText = t(titleKey).replace(/\n/g, " ");
  const top1 = storyTopMargin + (storyHeight * topPct) / 100;
  const top2 = storyTopMargin + (storyHeight * topPct2) / 100;
  const lineGap = compact ? 10 : 14;
  const hLineWidth = compact ? 28 : 56;
  const dashedColor = "#04d3bc";
  const dashedOpacity = 0.7;
  const labelStyle = {
    fontSize: compact ? 13 : 15,
    fontWeight: 600,
    color: "rgb(45, 55, 72)",
    lineHeight: 1.35,
    textAlign: "left",
    whiteSpace: "nowrap",
  };
  const hLineStyle = {
    width: hLineWidth,
    flexShrink: 0,
    borderTop: `2px dashed ${dashedColor}`,
    opacity: dashedOpacity,
  };

  return (
    <div key={titleKey} className={plancheScrollItemClass(delay, animated)} style={{ position: "absolute", inset: 0, zIndex: 2 }}>
      <div
        style={{
          position: "absolute",
          top: top1,
          height: Math.max(top2 - top1, 0),
          right: hLineWidth,
          borderLeft: `2px dashed ${dashedColor}`,
          opacity: dashedOpacity,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: top1,
          left: 0,
          right: 0,
          transform: "translateY(-50%)",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <div style={labelStyle}>{fullText}</div>
        <div style={{ width: lineGap, flexShrink: 0 }} />
        <div style={hLineStyle} />
      </div>
      <div
        style={{
          position: "absolute",
          top: top2,
          right: 0,
          transform: "translateY(-50%)",
          ...hLineStyle,
        }}
      />
    </div>
  );
}

function renderPlancheSideAnnotation(t, { titleKey, delay, side, topPct, topPctCompactLaptop, nowrap = false }, { storyTopMargin, storyHeight, compact = false, compactLaptop = false, animated = true }) {
  const isLeft = side === "left";
  const effectiveTopPct = compactLaptop && topPctCompactLaptop != null ? topPctCompactLaptop : topPct;
  const anchorTop = storyTopMargin + (storyHeight * effectiveTopPct) / 100;
  const lineGap = compact ? 10 : 14;
  const labelStyle = {
    fontSize: compact ? 13 : 15,
    fontWeight: 600,
    color: "rgb(45, 55, 72)",
    lineHeight: 1.35,
    textAlign: "left",
    ...(nowrap ? { whiteSpace: "nowrap", flexShrink: 0 } : { flex: "0 1 auto", minWidth: 0 }),
  };
  const lineStyle = {
    flex: 1,
    minWidth: compact ? 10 : 20,
    maxWidth: compact ? 28 : 56,
    borderTop: "2px dashed #04d3bc",
    opacity: 0.7,
  };
  const calloutStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flex: "0 1 auto",
    minWidth: 0,
  };

  return (
    <div
      key={titleKey}
      className={plancheScrollItemClass(delay, animated)}
      style={{
        position: "absolute",
        top: anchorTop,
        left: 0,
        right: 0,
        transform: "translateY(-50%)",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        zIndex: 2,
      }}
    >
      {isLeft ? (
        <>
          <div style={{ ...calloutStyle, flex: 1, justifyContent: "flex-end" }}>
            <div style={labelStyle}>{t(titleKey)}</div>
          </div>
          <div style={{ width: lineGap, flexShrink: 0 }} />
          <div style={lineStyle} />
        </>
      ) : (
        <>
          <div style={lineStyle} />
          <div style={{ width: lineGap, flexShrink: 0 }} />
          <div style={{ ...calloutStyle, flex: 1, justifyContent: "flex-start" }}>
            <div style={labelStyle}>{t(titleKey)}</div>
          </div>
        </>
      )}
    </div>
  );
}

function renderPlancheDecorImage({ src, top, side, width, rotate, compact, delay, right, left, animated = true }) {
  const horizontal = left !== undefined
    ? { left }
    : right !== undefined
      ? { right }
      : side === "left"
        ? { left: compact ? "-4px" : "-18px" }
        : { right: compact ? "-4px" : "-22px" };

  return (
    <img
      key={src}
      src={src}
      alt=""
      className={plancheScrollItemClass(delay, animated)}
      style={{
        position: "absolute",
        top,
        ...horizontal,
        width,
        height: "auto",
        transform: `rotate(${rotate}deg)`,
        pointerEvents: "none",
        zIndex: 0,
        filter: "drop-shadow(0 10px 18px rgba(0, 0, 0, 0.14))",
      }}
    />
  );
}

function renderPlancheSideDecor(side, { compact, storyTopMargin, storyHeight, deviceBgHeight, visualScale = 1, animated = true }) {
  const s = (value) => scaleHpDesktopVisual(value, visualScale);
  const toColumnTop = (px) => `${(px / deviceBgHeight) * 100}%`;
  const videoAnnotationY = storyTopMargin + (storyHeight * 20) / 100;
  const butterTopPx = videoAnnotationY + (compact ? 42 : s(50));

  const decorBySide = {
    left: [
      { src: "/images/butter.webp", top: toColumnTop(butterTopPx), right: compact ? "36px" : `${s(52)}px`, width: compact ? 28 : s(58), rotate: -14, delay: "150" },
      { src: "/images/salad.webp", top: compact ? "54%" : "58%", right: compact ? "58px" : `${s(84)}px`, width: compact ? 44 : s(88), rotate: 10, delay: "250" },
    ],
    right: [
      { src: "/images/cheese.webp", top: compact ? "42%" : "40%", right: compact ? "126px" : `${s(152)}px`, width: compact ? 46 : s(86), rotate: 16, delay: "150" },
      { src: "/images/ham.webp", top: compact ? "22%" : "20%", right: compact ? "166px" : `${s(204)}px`, width: compact ? 50 : s(94), rotate: -12, delay: "250" },
    ],
  };

  return decorBySide[side].map((item) => renderPlancheDecorImage({ ...item, side, compact, animated }));
}

function renderPlancheMobileFeatureItem(t, { emoji, titleKey, delay }, animated = true) {
  return (
    <div
      key={titleKey}
      className={plancheScrollItemClass(delay, animated)}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        width: "100%",
        boxSizing: "border-box",
        textAlign: "left",
      }}
    >
      <span style={{ fontSize: 22, flexShrink: 0, lineHeight: 1, display: "flex", alignItems: "center" }}>{emoji}</span>
      <div style={{
        fontSize: 15,
        fontWeight: 600,
        color: "rgb(45, 55, 72)",
        lineHeight: 1.45,
        textAlign: "left",
        flex: 1,
        minWidth: 0,
      }}>
        {t(titleKey).replace(/\n/g, " ")}
      </div>
    </div>
  );
}

function renderPlancheDiscoveryLayout({
  t,
  Player,
  deviceBgHeight,
  storyTopMargin,
  storyHeight,
  compact = false,
  frameWidth = 274,
  playerWidth = 244,
  frameMarginLeft = 14,
  plancheWidth = 160,
  visualScale = 1,
  compactLaptop = false,
  animated = true,
  storyId = "cafeuprealitychain1",
}) {
  const phoneFrameProps = { frameWidth, playerWidth, frameMarginLeft, plancheWidth, storyId };
  const annotationLayout = { storyTopMargin, storyHeight, compact, compactLaptop, animated };
  const phoneScrollClass = animated ? "scroll-scale-in" : "";
  const phoneScrollDelay = animated ? "200" : "";
  if (compact) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: 360, margin: "0 auto" }}>
        {renderPlanchePhoneFrame({
          Player,
          t,
          deviceBgHeight,
          storyTopMargin,
          storyHeight,
          scrollClass: phoneScrollClass,
          scrollDelay: phoneScrollDelay,
          matchMobileHeroFrame: true,
          ...phoneFrameProps,
        })}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 24, width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%", maxWidth: 320 }}>
            {INSTANT_AD_PLANCHE_ITEMS.map((item) => renderPlancheMobileFeatureItem(t, item, animated))}
          </div>
        </div>
      </div>
    );
  }

  const decorLayout = { compact, storyTopMargin, storyHeight, deviceBgHeight, visualScale, animated };
  const sideColumnStyle = {
    flex: compact ? "1 1 108px" : "1 1 280px",
    maxWidth: compact ? 160 : 360,
    minWidth: compact ? 96 : 200,
    position: "relative",
    height: deviceBgHeight,
    flexShrink: compact ? 1 : 0,
    overflow: "visible",
  };

  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 1100, margin: "0 auto", overflow: "visible" }}>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "center", width: "100%", gap: compact ? 4 : 16 }}>
        <div style={sideColumnStyle}>
          {renderPlancheSideDecor("left", decorLayout)}
          {INSTANT_AD_PLANCHE_ITEMS.filter((item) => item.side === "left").map((item) =>
            item.multiline
              ? renderPlancheMultilineLeftAnnotation(t, item, annotationLayout)
              : renderPlancheSideAnnotation(t, item, annotationLayout)
          )}
        </div>
        {renderPlanchePhoneFrame({
          Player,
          t,
          deviceBgHeight,
          storyTopMargin,
          storyHeight,
          scrollClass: phoneScrollClass,
          scrollDelay: phoneScrollDelay,
          ...phoneFrameProps,
        })}
        <div style={sideColumnStyle}>
          {renderPlancheSideDecor("right", decorLayout)}
          {INSTANT_AD_PLANCHE_ITEMS.filter((item) => item.side === "right").map((item) =>
            renderPlancheSideAnnotation(t, item, annotationLayout)
          )}
        </div>
      </div>
    </div>
  );
}

function renderPlanchePhoneFrame({
  Player,
  t,
  deviceBgHeight,
  storyTopMargin,
  storyHeight,
  frameWidth = 274,
  playerWidth = 244,
  frameMarginLeft = 14,
  plancheWidth = 160,
  scrollClass = "",
  scrollDelay = "",
  matchMobileHeroFrame = false,
  storyId = "cafeuprealitychain1",
}) {
  const wrapperClass = [scrollClass, scrollDelay ? `scroll-delay-${scrollDelay}` : ""].filter(Boolean).join(" ");
  const player = (
    <Player
      fromMaker={false}
      fromDemo={"middle"}
      mini={true}
      storyId={storyId}
      windowDimensions={getHpPhonePlayerBleedSize(playerWidth, storyHeight)}
    />
  );
  const phoneFrame = matchMobileHeroFrame ? (
    <div style={getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight)}>
      <div style={getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin)}>
        {player}
      </div>
      {renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight, frameAlt: t("PhoneFrameAlt") })}
    </div>
  ) : (
    <div style={getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight)}>
      <div style={getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin)}>
        {player}
      </div>
      {renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight, frameAlt: t("PhoneFrameAlt") })}
    </div>
  );
  return (
    <div className={wrapperClass || undefined} style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
      {phoneFrame}
      <img
        src="/images/planche.webp"
        alt=""
        style={{ width: plancheWidth, marginTop: 0, display: "block", pointerEvents: "none" }}
      />
    </div>
  );
}

function renderInstantAdCreateStepsList(t, { analyticsManager, setShowCreate1Clic } = {}) {
  return (
    <div style={{ width: "100%", maxWidth: 680, alignSelf: "stretch", textAlign: "left" }}>
      {INSTANT_AD_CREATE_STEPS.map((item, index) => renderInstantAdCreateStep(t, item, index === INSTANT_AD_CREATE_STEPS.length - 1, { analyticsManager, setShowCreate1Clic }))}
    </div>
  );
}

function renderInstantAdCreateStepDet(t, { detKey, detVariant }, { analyticsManager, setShowCreate1Clic } = {}) {
  if (detVariant === "create1ClicLink") {
    if (setShowCreate1Clic) {
      const openCreate1ClicDialog = () => {
        analyticsManager?.logOpenPopupTag(null, "create1Click_hp_benefit21", true);
        setShowCreate1Clic("create1Click_hp_benefit21");
      };
      return (
        <>
          {t("InstantAdSubBenefit21detPrefix")}
          <button
            type="button"
            onClick={openCreate1ClicDialog}
            style={{
              font: "inherit",
              fontStyle: "inherit",
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            {t("InstantAdSubBenefit21detLink")}
          </button>
          {t("InstantAdSubBenefit21detSuffix")}
        </>
      );
    }
    return `${t("InstantAdSubBenefit21detPrefix")}${t("InstantAdSubBenefit21detLink")}${t("InstantAdSubBenefit21detSuffix")}`;
  }
  return detKey ? t(detKey) : null;
}

function renderInstantAdCreateStep(t, { step, titleKey, detKey, detVariant, delay }, isLast = false, dialogHandlers = {}) {
  const detContent = renderInstantAdCreateStepDet(t, { detKey, detVariant }, dialogHandlers);
  return (
    <div
      key={step}
      className={`scroll-benefit-item scroll-delay-${delay}`}
      style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 18, marginBottom: isLast ? 0 : 4, width: "100%" }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 32 }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: "#04d3bc",
          color: "#ffffff",
          fontWeight: 700,
          fontSize: 15,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {step}
        </div>
        {!isLast ? (
          <div style={{ width: 2, flex: 1, minHeight: 20, marginTop: 6, backgroundColor: "#d9e2ec", borderRadius: 1 }} />
        ) : null}
      </div>
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.45, paddingBottom: isLast ? 0 : 18, paddingTop: 2, textAlign: "left" }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "rgb(45, 55, 72)" }}>{t(titleKey)}</div>
        {detContent ? (
          <div style={{ fontSize: 15, color: "#718096", marginTop: 6, fontStyle: "italic", lineHeight: 1.5 }}>
            {detContent}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const MainHPConfig = {
  homePageOrigin: null,
  pageTitle: 'ProductPageAdTitle',
  origin: 'home_page_ad',
  hasWebDemo: true, // Cette page a maintenant la section de comparaison avec web_demo
  
  // Configuration FAQ
  faqConfig: [
    { theme: "socialAdDest" },
    { theme: "socialWebsiteVideo" },
    { theme: "socialProfileRedirect" },
    { theme: "alreadyWebsite" },
    { theme: "easyToUse" }
  ],
  
  // Configuration des données FAQ
  faqData: (t) => ({
    title: t("FAQCatUprealityOffer"),
    rows: [
      {
        title: t("FAQSocialAdDestQuestion"),
        content: t("FAQSocialAdDestAnswer"),
      },
      {
        title: t("FAQSocialWebsiteVideoQuestion"),
        content: t("FAQSocialWebsiteVideoAnswer"),
      },
      {
        title: t("FAQSocialProfileRedirectQuestion"),
        content: [
          t("FAQSocialProfileRedirectAnswer"),
          "\n\n- " + t("FAQSocialProfileRedirectAnswerl0"),
          "\n- " + t("FAQSocialProfileRedirectAnswerl1"),
          "\n- " + t("FAQSocialProfileRedirectAnswerl2"),
          "\n\n" + t("FAQSocialProfileRedirectAnswerAfter"),
        ].join(""),
      },
      {
        title: t("FAQWebsiteSubstituteQuestion"),
        content: t("FAQWebsiteSubstituteAnswer"),
      },
      {
        title: t("FAQEasyToUseQuestion"),
        content: t("FAQEasyToUseAnswer"),
      },
    ],
  }),

  // Schema.org FAQPage pour GEO
  faqSchema: (t) => {
    const d = MainHPConfig.faqData(t);
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: d.rows.map((r) => ({
        "@type": "Question",
        name: r.title,
        acceptedAnswer: { "@type": "Answer", text: r.content },
      })),
    };
  },

  // Rendu desktop
  desktopRenderer: (props) => {
    const { 
      t, navigate, language, analyticsManager, MaterialButton, ImageModal, 
      Player, WebPageMock, TestimonialsSection, Faq, styles,
      showLogin, setShowLogin, loginTitle, setLoginTitle, showCreate1Clic, setShowCreate1Clic, isDesktopDemoHovered, setIsDesktopDemoHovered, onLogin,
      viewportHeight = window.innerHeight,
      viewportWidth = window.innerWidth,
    } = props;
    const langPrefix = language === "fr" ? "/fr" : "/en";

    // Fonctions pour les analytics
    const handleWebDemoClick = () => {
      if (analyticsManager && analyticsManager.logClickInWebDemoTag) {
        analyticsManager.logClickInWebDemoTag();
      }
    };

    const handleWebDemoScroll = () => {
      if (analyticsManager && analyticsManager.logScrollInWebDemoTag) {
        analyticsManager.logScrollInWebDemoTag();
      }
    };

    const deskTop = viewportHeight >= 80 + 16 + 576 + 16;
    const appBarHeight = deskTop ? 80 : 60;
    const offsetToIndicateScroll = deskTop ? 100 : 20;
    const logoHeight = 42;
    const compareSlideCompactDesktop = viewportHeight >= HP_DESKTOP_VISUAL_COMPACT_MAX_HEIGHT;
    const compactLaptop = !compareSlideCompactDesktop;
    const {
      deviceBgHeight,
      storyHeight,
      storyTopMargin,
      frameWidth,
      playerWidth,
      frameMarginLeft,
      plancheWidth,
      heroArrowWidth,
      heroCalloutMarginTop,
      heroCalloutMaxWidth,
      createSlideImageHeight,
      createSlideVideoWidth,
      scale: visualScale,
    } = getHpDesktopVisualMetrics(deskTop, { compactLaptop });
    // Espace texte → image/player : réduit en priorité si la largeur manque (reste inchangé)
    const heroTextWidth = 540;
    const heroLeftInset = 16; // marge incompressible à gauche du bloc titre
    const heroTextToVisualGapPreferred = 56;
    const heroTextToVisualGapMin = 8;
    const heroIgApproxWidth = Math.round(deviceBgHeight * 0.48);
    const heroRowFixedApprox = heroLeftInset + heroTextWidth + heroIgApproxWidth + frameWidth + heroCalloutMaxWidth + 12 + 12 + 48;
    const heroTextToVisualGap = Math.max(
      heroTextToVisualGapMin,
      Math.min(heroTextToVisualGapPreferred, viewportWidth - heroRowFixedApprox)
    );
    const compareTitleWrapperStyle = compareSlideCompactDesktop
      ? { flexShrink: 0, width: '100%', paddingTop: 80, paddingBottom: 80 }
      : { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, width: '100%', padding: '28px 0', boxSizing: 'border-box' };
    const compareTitleStyle = {
      ...instantAdSlideTitleStyle,
      margin: compareSlideCompactDesktop ? 0 : '18px 0',
      width: '100%',
      flexShrink: 0,
    };
    // Compact : haut = 28 shell + 18 marge ; bas = 46 (comme slide 2). Non-compact : 80 / 80 (comme slide 2 padding titre)
    const slideTitleStyle = compactLaptop
      ? { ...instantAdSlideTitleStyle, marginTop: 18, marginBottom: 46 }
      : { ...instantAdSlideTitleStyle, marginBottom: 80 };
    const slideShellOpts = { compact: compactLaptop };
    const floatingIndicatorWrapperStyle = {
      position: "absolute",
      top: "30%",
      right: 0,
      transform: "translateY(-50%)",
      zIndex: 30,
      overflow: "hidden",
      width: "fit-content",
      display: "flex",
      justifyContent: "flex-end",
      pointerEvents: "none",
    };
    const floatingIndicatorStyle = {
      transform: isDesktopDemoHovered ? "translateX(105%)" : "translateX(0)",
      transition: "transform 180ms ease-out",
      width: "fit-content",
      display: "inline-block",
      zIndex: 30,
      backgroundColor: "#ffffff",
      border: "1px solid #d9efe9",
      borderRight: "none",
      borderTopLeftRadius: 14,
      borderBottomLeftRadius: 14,
      padding: "15px 22px 15px 16px",
      boxShadow: "0 10px 24px rgba(4, 211, 188, 0.18)",
      fontSize: 16,
      fontWeight: 700,
      color: "#1f2d3d",
      whiteSpace: "nowrap",
      pointerEvents: "none",
    };
    const floatingIndicatorId = "homepage-floating-indicator";
    const isSafariMacOS = isMacOS() && isSafari();
    const hasRectOverlap = (rectA, rectB) => {
      return !(
        rectA.right < rectB.left ||
        rectA.left > rectB.right ||
        rectA.bottom < rectB.top ||
        rectA.top > rectB.bottom
      );
    };
    const dismissFloatingIndicator = () => {
      setIsDesktopDemoHovered(true);
    };
    const handleDemoMouseEnter = (e) => {
      // Safari macOS : une fois masquée, ne pas réévaluer (évite le retour à l'ouverture des options)
      if (isSafariMacOS && isDesktopDemoHovered) {
        return;
      }
      const indicator = document.getElementById(floatingIndicatorId);
      if (!indicator) {
        if (!isSafariMacOS) {
          setIsDesktopDemoHovered(false);
        }
        return;
      }
      const demoRect = e.currentTarget.getBoundingClientRect();
      const indicatorRect = indicator.getBoundingClientRect();
      const overlaps = hasRectOverlap(demoRect, indicatorRect);
      if (isSafariMacOS) {
        if (overlaps) {
          dismissFloatingIndicator();
        }
      } else {
        setIsDesktopDemoHovered(overlaps);
      }
    };

    return (
      <div style={{display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'center', alignContent: 'center', justifyContent: 'center', background: HP_SLIDE_BG_HERO, color: "rgb(45, 55, 72)" }}>
        <div style={floatingIndicatorWrapperStyle}>
          <div id={floatingIndicatorId} style={floatingIndicatorStyle}>
            <div style={{display: "flex", alignItems: "center", gap: 8}}>
              <div style={{width: 24, height: 24, borderRadius: "50%", backgroundColor: "#2fc4b5", display: "flex", alignItems: "center", justifyContent: "center"}}>
                <TrendingUpIcon style={{fontSize: 16, color: "#ffffff"}} />
              </div>
              <span>{t("HomeFloatingConversionTarget")}</span>
            </div>
          </div>
        </div>
        <div style={{width: "100%", padding: "16px 24px", boxSizing: "border-box", background: "rgba(255,255,255,0.92)", display: "flex", justifyContent: "center"}}>
          <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", alignContent: "center", width: "100%", maxWidth: 1160}}>
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", cursor: "pointer"}} onClick={()=>{analyticsManager.logClickLogoTag();navigate(`${langPrefix}`);}}>
              <img src="/images/logo_gradient_transparent.png" alt="UpReality" height={logoHeight} style={{display: "block", height: logoHeight, width: "auto"}} />
            </div>
            <div style={{display: "flex", flexDirection: "row", alignContent: "center", justifyContent: "center", alignItems: "center", gap: 10}}>
              <button type="button" className="topbar-btn topbar-btn-active" aria-current="page">{t("TopbarHome")}</button>
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{analyticsManager.logClickPricingTag();navigate("/Pricing");}}>{t("Pricing")}</button>
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{navigate(`${langPrefix}/about`);}}>{t("AboutUs")}</button>
              <div style={{width: 7, height: 7, borderRadius: "50%", backgroundColor: "#04d3bc", margin: "0 6px"}} />
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{analyticsManager.logOpenPopupTag(null, "home_ad_login", true);setShowLogin("login");setLoginTitle(t('Login'));}}>{t("Login")}</button>
              <button type="button" className="topbar-btn topbar-btn-primary" onClick={()=>{analyticsManager.logOpenPopupTag(null, "home_ad_create_account", true);setShowLogin("account");setLoginTitle(t('CreateAccount'));}}>{t("CreateAccount")}</button>
            </div>
          </div>
        </div>
        <div className="FirstSectionContainer" style={{minHeight: viewportHeight - offsetToIndicateScroll - appBarHeight, width: '100%', display: "flex", flexDirection: "column", justifyContent: 'center', background: HP_SLIDE_BG_HERO, boxSizing: "border-box"}}>             
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start'}}>
            <div className="scroll-hero" style={{width: heroTextWidth, flexShrink: 0, marginTop: 100, marginLeft: heroLeftInset}}>
              <h1 style={{whiteSpace: "nowrap"}}>{t("InstantAdHero1")}&nbsp;<div className="yellowParallelogram"><div className="textOverYellowParallelogram">{t("InstantAdHero2")}</div></div>{t("InstantAdHero3")}</h1>
              <div className="scroll-fade-up scroll-delay-200">{t("InstantAdHero41")} {t("InstantAdHero42")}</div>
              <br/><br/><br/>      
              <div className="scroll-cta scroll-delay-300">
                <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemo")} value={t("CTA")} /> 
                <div style={{marginTop: 20}}/> 
                <MaterialButton style="outlined-primary" onClick={()=>{analyticsManager.logHPTryMakerTag("top_ad");navigate("/maker");}}  value={t("InstantAdCTA")} /> <br/> 
              </div>
            </div>
            <div className="scroll-fade-right scroll-delay-200">
              <div style={{marginLeft: heroTextToVisualGap}}>      
                <img src={"/images/from_ig_ig_profile_AD.webp"} alt={t('FromInstagramProfileAlt')} style={{height: deviceBgHeight}} />
              </div>
            </div>
            {<div
              className="scroll-scale-in scroll-delay-400"
              style={{marginLeft: 12, display: "flex", flexDirection: "row", alignItems: "flex-start"}}
              onMouseEnter={handleDemoMouseEnter}
              onMouseLeave={isSafariMacOS ? undefined : () => setIsDesktopDemoHovered(false)}
              onClick={isSafariMacOS ? dismissFloatingIndicator : undefined}
            >
              <div style={getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight)}>
                <div style={getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin)}>
                  <Player fromMaker={false} fromDemo={"top"} mini={true} storyId={"cafeuprealityad"} windowDimensions={getHpPhonePlayerBleedSize(playerWidth, storyHeight)} />
                </div>
                {renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight, frameAlt: t("PhoneFrameAlt") })}
              </div>
              <div
                style={{
                  marginLeft: 12,
                  marginTop: heroCalloutMarginTop,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  maxWidth: heroCalloutMaxWidth,
                  pointerEvents: "none",
                }}
              >
                <img
                  src="/images/top_left_arrow.png"
                  alt=""
                  style={{width: heroArrowWidth, height: "auto", marginBottom: 6}}
                />
                <div style={{fontSize: 15, fontWeight: 700, color: "#1f2d3d", lineHeight: 1.35}}>
                  {t("InstantAdYourContentHere")}
                </div>
                <div style={{fontSize: 13, fontWeight: 500, color: "#64748b", lineHeight: 1.35, marginTop: 2}}>
                  {t("InstantAdYourContentNotAI")}
                </div>
              </div>
            </div>}                                                                                    
          </div>
        </div>
        {
          <div className="scroll-slide-up" style={{marginTop: 0, minHeight: viewportHeight, width: '100%', backgroundColor: INSTANT_AD_BG_WHITE, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', paddingBottom: 24}}>
            <StructuredData id="compare-instant-ad" data={{
              "@context": "https://schema.org",
              "@type": "Table",
              "about": { "@type": "Thing", "name": t('CompareWebsiteRestaurants') },
              "abstract": `${t('WebsiteQ1')}, ${t('WebsiteQ2')}, ${t('WebsiteQ3')} | ${t('StoryQ1')}, ${t('StoryQ2')}, ${t('StoryQ3')}`
            }} />
            <div style={compareTitleWrapperStyle}>
              <h2 style={compareTitleStyle}>{t('CompareWebsiteRestaurants')}</h2>
            </div>
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 16, padding: '0 24px', boxSizing: 'border-box' }}>
              <div style={{display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center', alignContent: 'center', justifyContent: 'center', flexShrink: 0, gap: 48}}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight)}>
                    <div style={getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin)}>
                      <WebPageMock 
                        id="web_demo" 
                        open={true} 
                        url={`/demoWeb2/index.html?lang=${detectBrowserLanguage()}`} 
                        width={getHpPhonePlayerBleedSize(playerWidth, storyHeight).width}
                        height={getHpPhonePlayerBleedSize(playerWidth, storyHeight).height}
                        imageOverWebsite={false} 
                        close={()=>{}}
                        onClick={handleWebDemoClick}
                        onScroll={handleWebDemoScroll}
                      />
                    </div>
                    {renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight })}
                  </div>
                  <div style={{marginTop: 10, fontSize: 14, fontStyle: 'italic', color: '#8a94a6', textAlign: 'center'}}>{t('CompareFrameTraditionalWebsite')}</div>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 64, maxWidth: 560, minWidth: 420}}>
                  {[
                    ['CompareDuel1', 'WebsiteQ1', 'StoryQ1'],
                    ['CompareDuel2', 'WebsiteQ2', 'StoryQ2'],
                    ['CompareDuel3', 'WebsiteQ3', 'StoryQ3'],
                  ].map(([label, left, right]) => (
                    <div key={label} style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                      <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 8}}>
                        <div style={{width: 96, borderTop: '1px dashed #c9d4e0', opacity: 0.6}}/>
                        <div style={{backgroundColor: 'rgba(4, 211, 188, 0.18)', color: '#0a9c8b', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, padding: '6px 16px', borderRadius: 999, whiteSpace: 'nowrap'}}>{t(label)}</div>
                        <div style={{width: 96, borderTop: '1px dashed #c9d4e0', opacity: 0.6}}/>
                      </div>
                      <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%', textAlign: 'center'}}>
                        <div style={{width: '50%', paddingRight: 16, boxSizing: 'border-box', fontStyle: 'italic', color: '#8a94a6', lineHeight: 1.4}}>{t(left)}</div>
                        <div style={{width: '50%', paddingLeft: 16, boxSizing: 'border-box', fontWeight: 700, color: 'rgb(45, 55, 72)', lineHeight: 1.4}}>{t(right)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight)}>      
                    <div style={getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin)}>                                                  
                      <Player fromMaker={false} fromDemo={"bottom"} mini={true} storyId={"cafeupreality"} windowDimensions={getHpPhonePlayerBleedSize(playerWidth, storyHeight)} />
                    </div>
                    {renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight })}
                  </div>
                  <div style={{marginTop: 10, fontSize: 14, fontStyle: 'italic', color: '#8a94a6', textAlign: 'center'}}>{t('CompareFrameSocialWebsite')}</div>
                </div> 
              </div>
              <CompareWebsiteClosing compact spacious={deskTop} variant="restaurants" />
            </div>
            {compareSlideCompactDesktop && <div style={{ flex: 1, minHeight: 0 }} />}
          </div>
        }
        <div className="scroll-slide-up" style={{ ...instantAdSlideShellStyle(INSTANT_AD_BG_GREY, slideShellOpts), overflow: "visible" }}>
          <h2 style={slideTitleStyle}>{t("RestaurantsBenefit1")}</h2>
          {renderPlancheDiscoveryLayout({
            t,
            Player,
            deviceBgHeight,
            storyTopMargin,
            storyHeight,
            frameWidth,
            playerWidth,
            frameMarginLeft,
            plancheWidth,
            visualScale,
            compactLaptop,
          })}
        </div>
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(INSTANT_AD_BG_WHITE, slideShellOpts)}>
          <h2 style={slideTitleStyle}>
            {t("InstantAdBenefit2")}
          </h2>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", paddingRight: 40, minWidth: 0 }}>
              <div className="scroll-fade-left scroll-delay-100" style={{ flex: "0 0 auto" }}>
                <ImageContainer 
                  src={language === "fr" ? "/images/HP-screenshot2.webp" : "/images/HP-screenshot2-en.webp"}
                  alt={t('StoryMakerPagePreviewAlt')}
                  height={createSlideImageHeight}
                  variant="clean"
                  delay="100"
                  videoSrc={encodeURI("/images/creer_sa_landing_page_sociale.mp4")}
                  videoWidth={createSlideVideoWidth}
                  videoAnalyticsOrigin="home_ad_maker_video"
                />
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-start", paddingLeft: 40, minWidth: 0 }}>
              <div style={{ paddingTop: 4, width: "100%" }}>
                {renderInstantAdCreateStepsList(t, { analyticsManager, setShowCreate1Clic })}
              </div>
            </div>
          </div>
        </div>
        {renderWhoForMockupSlide(t, { MaterialButton, analyticsManager, navigate, denseTop: compactLaptop, backgroundColor: HP_SLIDE_BG_HERO })}
        <div className="scroll-slide-up" style={{ marginTop: 0, width: "100%", backgroundColor: INSTANT_AD_BG_WHITE }}>
          <TestimonialsSection badgeKey="KFontaineTestimonialBadge" subtitleKey="TestimonialsSubtitleRestaurants" compact={compactLaptop} />
        </div>
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(INSTANT_AD_BG_GREY, slideShellOpts)}>
          <div style={{ width: 800, maxWidth: "100%", boxSizing: "border-box", marginTop: compactLaptop ? 18 : 0 }}>
            <StructuredData id="faq-instant-ad" data={MainHPConfig.faqSchema(t)} />
            <div className="hp-faq-on-grey">
              <Faq
                data={MainHPConfig.faqData(t)}
                styles={styles}
                config={{}}
              />
            </div>
          </div>
          <div className="scroll-fade-up" style={{ marginTop: 48, display: 'flex', flexDirection: 'row', justifyContent: 'center', width: "100%" }}>
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemoFaq")} value={t("CTA")} />
              <div style={{marginTop: 20}}/>
              <MaterialButton style="outlined-primary" onClick={()=>{analyticsManager.logHPTryMakerTag("faq_ad");navigate("/maker");}}  value={t("InstantAdCTA")} />
            </div>
          </div>
        </div>
        <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "100%", backgroundColor: "#b2ede7"}}>
          <div style={{textAlign: "center", marginTop: 20, marginBottom: 16, backgroundColor: "#b2ede7"}}>
          <a style={{color: "rgb(45, 55, 72)", textDecoration: "none"}} href="/terms/cgu.pdf" target="_blank" rel="noreferrer" onClick={()=>{analyticsManager.logReadCGUTag("home_page_ad");}}>{t("Terms")}</a> - <a style={{color: "rgb(45, 55, 72)", textDecoration: "none"}} href="/terms/charte_confidentialite.pdf" target="_blank" rel="noreferrer" onClick={()=>{analyticsManager.logReadPrivacyTag("home_page_ad");}}>{t("Privacy")}</a> - <a style={{color: "rgb(45, 55, 72)", textDecoration: "none"}} href="/terms/cgv.pdf" target="_blank" rel="noreferrer" onClick={()=>{analyticsManager.logReadCGVTag("home_page_ad");}}>{t("CGV")}</a> - <FooterLinkedInLink t={t} />
          </div>
        </div>
      </div>
    );
  },

  // Rendu mobile
  mobileRenderer: (props) => {
    const { 
      t, navigate, language, analyticsManager, MaterialButton, LineBreak, 
      Player, WebPageMock, TestimonialsSection, Faq, styles,
      showLogin, setShowLogin, loginTitle, setLoginTitle, showCreate1Clic, setShowCreate1Clic, onLogin
    } = props;
    const langPrefix = language === "fr" ? "/fr" : "/en";
    const floatingIndicatorStyle = {
      position: "absolute",
      top: 370,
      right: 0,
      transform: "translateY(-50%)",
      zIndex: 30,
      backgroundColor: "#ffffff",
      border: "1px solid #d9efe9",
      borderRight: "none",
      borderTopLeftRadius: 14,
      borderBottomLeftRadius: 14,
      padding: "10px 15px 10px 10px",
      boxShadow: "0 10px 24px rgba(4, 211, 188, 0.18)",
      fontSize: 13,
      fontWeight: 700,
      color: "#1f2d3d",
      whiteSpace: "nowrap",
      pointerEvents: "none",
    };

    // Fonctions pour les analytics
    const handleWebDemoClick = () => {
      if (analyticsManager && analyticsManager.logClickInWebDemoTag) {
        analyticsManager.logClickInWebDemoTag();
      }
    };

    const handleWebDemoScroll = () => {
      if (analyticsManager && analyticsManager.logScrollInWebDemoTag) {
        analyticsManager.logScrollInWebDemoTag();
      }
    };

    return (
      <div style={{display: 'flex', flexDirection: 'column', width: "100%", alignItems: 'center', alignContent: 'center', justifyContent: 'center', background: HP_SLIDE_BG_HERO, color: "rgb(45, 55, 72)" }}>
        <div style={floatingIndicatorStyle}>
          <div style={{display: "flex", alignItems: "center", gap: 8}}>
            <div style={{width: 20, height: 20, borderRadius: "50%", backgroundColor: "#2fc4b5", display: "flex", alignItems: "center", justifyContent: "center"}}>
              <TrendingUpIcon style={{fontSize: 12, color: "#ffffff"}} />
            </div>
            <span>{t("HomeFloatingConversionTarget")}</span>
          </div>
        </div>
        <div style={{width: "100%", padding: "14px 16px", boxSizing: "border-box", background: "rgba(255,255,255,0.92)"}}>
          <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", cursor: "pointer"}} onClick={()=>{analyticsManager.logClickLogoTag();navigate(`${langPrefix}`);}}>
            <img src="/images/logo_gradient_transparent.png" alt="UpReality" height={46} style={{display: "block", height: 46, width: "auto"}} />
          </div>
          <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 24}}>
            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: "wrap"}}>
              <button type="button" className="topbar-btn topbar-btn-active" aria-current="page">{t("TopbarHome")}</button>
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{analyticsManager.logClickPricingTag();navigate("/Pricing");}}>{t("Pricing")}</button>
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{navigate(`${langPrefix}/about`);}}>{t("AboutUs")}</button>
            </div>
            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: "wrap"}}>
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{analyticsManager.logOpenPopupTag(null, "home_ad_login", true);setShowLogin("login");setLoginTitle(t('Login'));}}>{t("Login")}</button>
              <button type="button" className="topbar-btn topbar-btn-primary" onClick={()=>{analyticsManager.logOpenPopupTag(null, "home_ad_create_account", true);setShowLogin("account");setLoginTitle(t('CreateAccount'));}}>{t("CreateAccount")}</button>
            </div>
          </div>
        </div>
        <br/>
        <div style={{paddingLeft: 16, paddingRight: 16}}><h2>{t("InstantAdHero1")}&nbsp;<div className="yellowParallelogram"><div className="textOverYellowParallelogram">{t("InstantAdHero2")}</div></div>{t("InstantAdHero3")}</h2></div>                
        <div style={{fontSize: 18, paddingLeft: 16, paddingRight: 16, marginBottom: 20}}>{t("InstantAdHero41")}{t("InstantAdHero42")}</div>                                
        <div style={{marginTop: 20}}>
          <div style={getHpPhoneFrameShellStyle(274, 576)}>
          <div style={getHpPhonePlayerInsetStyle(14, 15)}>    
            <Player fromMaker={false} fromDemo={"top"} mini={true} storyId={"cafeuprealityad"} windowDimensions={getHpPhonePlayerBleedSize(244, 540)} /> 
          </div>
          {renderHpPhoneFrameOverlay({ frameWidth: 274, deviceBgHeight: 576, frameAlt: t("PhoneFrameAlt"), id: "hp-phonebg-top" })}
          </div>
        </div>                    
        <img src={"/images/from_ig_ig_profile_mobile_AD.webp"} alt={t('FromInstagramProfileAlt')} width="300" style={{marginTop: 10, marginRight: 18}}/>                           
        <div style={{marginTop: 50}}/>          
        <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemo")} value={t("CTA")} />   
        <LineBreak/>             
        <MaterialButton style="outlined-primary" onClick={()=>{analyticsManager.logHPTryMakerTag("top_ad");navigate("/maker");}}  value={t("InstantAdCTA")} />
        <div style={{ marginBottom: 32 }} />
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(INSTANT_AD_BG_WHITE, { compact: true })}>
        <StructuredData id="compare-instant-ad" data={{
          "@context": "https://schema.org",
          "@type": "Table",
          "about": { "@type": "Thing", "name": t('CompareWebsiteRestaurants') },
          "abstract": `${t('WebsiteQ1')}, ${t('WebsiteQ2')}, ${t('WebsiteQ3')} | ${t('StoryQ1')}, ${t('StoryQ2')}, ${t('StoryQ3')}`
        }} />
        <h2 style={instantAdSlideTitleStyleCompact}>{t('CompareWebsiteRestaurants')}</h2> 
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 20}}>
          <div style={getHpPhoneFrameShellStyle(274, 576)}>
            <div style={getHpPhonePlayerInsetStyle(14, 15)}>                            
            <WebPageMock 
              id="web_demo" 
              open={true} 
              url={`/demoWeb2/index.html?lang=${detectBrowserLanguage()}`} 
              width={getHpPhonePlayerBleedSize(244, 540).width}
              height={getHpPhonePlayerBleedSize(244, 540).height}
              imageOverWebsite={false} 
              close={()=>{}}
              onClick={handleWebDemoClick}
              onScroll={handleWebDemoScroll}
            />
            </div>
            {renderHpPhoneFrameOverlay({ frameWidth: 274, deviceBgHeight: 576 })}
          </div>
          <div style={{marginTop: 8, fontSize: 14, fontStyle: 'italic', color: '#8a94a6', textAlign: 'center'}}>{t('CompareFrameTraditionalWebsite')}</div>
        </div>
        <div style={{marginTop: 10}}>
          <ul>
            <li>{t('WebsiteQ1')}</li>
            <li>{t('WebsiteQ2')}</li>
            <li>{t('WebsiteQ3')}</li>
          </ul>
        </div>    
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 40}}>
          <div style={getHpPhoneFrameShellStyle(274, 540)}>
            <div style={getHpPhonePlayerInsetStyle(14, 14)}>    
              <Player fromMaker={false} fromDemo={"bottom"} mini={true} storyId={"cafeupreality"} windowDimensions={getHpPhonePlayerBleedSize(244, 508)} /> 
            </div>
            {renderHpPhoneFrameOverlay({ frameWidth: 274, deviceBgHeight: 540, id: "hp-phonebg-bottom" })}
          </div>
          <div style={{marginTop: 8, fontSize: 14, fontStyle: 'italic', color: '#8a94a6', textAlign: 'center'}}>{t('CompareFrameSocialWebsite')}</div>
        </div> 
        <div style={{marginTop: 10}}>
          <ul>
            <li>{t('StoryQ1')}</li>
            <li>{t('StoryQ2')}</li>
            <li>{t('StoryQ3')}</li>
          </ul>
        </div>
        <CompareWebsiteClosing mobile variant="restaurants" />
        </div>
        <div className="scroll-slide-up" style={{ ...instantAdSlideShellStyle(INSTANT_AD_BG_GREY, { compact: true }), overflow: "visible" }}>
          <h2 style={instantAdSlideTitleStyleCompact}>{t("RestaurantsBenefit1")}</h2>
            {renderPlancheDiscoveryLayout({
              t,
              Player,
              deviceBgHeight: 576,
              storyTopMargin: 15,
              storyHeight: 540,
              compact: true,
            })}
        </div>
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(INSTANT_AD_BG_WHITE, { compact: true })}>
            <h2 style={instantAdSlideTitleStyleCompact}>{t("InstantAdBenefit2")}</h2>
            {renderInstantAdCreateStepsList(t, { analyticsManager, setShowCreate1Clic })}
        </div>
        {renderWhoForMockupSlide(t, { MaterialButton, analyticsManager, navigate, compact: true, backgroundColor: HP_SLIDE_BG_HERO })}
        <div className="scroll-slide-up" style={{ marginTop: 0, width: "100%", backgroundColor: INSTANT_AD_BG_WHITE }}>
        <TestimonialsSection badgeKey="KFontaineTestimonialBadge" subtitleKey="TestimonialsSubtitleRestaurants" compact />
        </div>
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(INSTANT_AD_BG_GREY, { compact: true })}>
          <StructuredData id="faq-instant-ad" data={MainHPConfig.faqSchema(t)} />
          <div className="hp-faq-on-grey">
            <Faq
              data={MainHPConfig.faqData(t)}
              styles={styles}
              config={{}}
            />
          </div>
          <div style={{ marginTop: 32, display: 'flex', flexDirection: 'row', justifyContent: 'center', width: "100%" }}>
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemoFaq")} value={t("CTA")} />
              <div style={{marginTop: 20}}/>
              <MaterialButton style="outlined-primary" onClick={()=>{analyticsManager.logHPTryMakerTag("faq_ad");navigate("/maker");}}  value={t("InstantAdCTA")} />
            </div>
          </div>
        </div>
        <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "100%", backgroundColor: "#b2ede7"}}>
          <div style={{textAlign: "center", marginTop: 16, marginBottom: 16, backgroundColor: "#b2ede7"}}>
            <a style={{color: "rgb(45, 55, 72)", textDecoration: "none"}} href="/terms/cgu.pdf" target="_blank" rel="noreferrer" onClick={()=>{analyticsManager.logReadCGUTag("home_page_ad");}}>{t("Terms")}</a> - <a style={{color: "rgb(45, 55, 72)", textDecoration: "none"}} href="/terms/charte_confidentialite.pdf" target="_blank" rel="noreferrer" onClick={()=>{analyticsManager.logReadPrivacyTag("home_page_ad");}}>{t("Privacy")}</a> - <a style={{color: "rgb(45, 55, 72)", textDecoration: "none"}} href="/terms/cgv.pdf" target="_blank" rel="noreferrer" onClick={()=>{analyticsManager.logReadCGVTag("home_page_ad");}}>{t("CGV")}</a> - <FooterLinkedInLink t={t} />
          </div>
        </div>
      </div>
    );
  }
};

export {
  renderPlancheDiscoveryLayout,
  instantAdSlideShellStyle,
  instantAdSlideTitleStyle,
  getHpDesktopVisualMetrics,
  HP_DESKTOP_VISUAL_COMPACT_MAX_HEIGHT,
};